import React, { useState, useMemo } from 'react';
import { format, isToday, isTomorrow, isThisWeek, isPast, startOfDay } from 'date-fns';
import { Calendar, ChevronDown, ChevronRight, Clock, AlertTriangle, CheckCircle, Filter, SortDesc } from 'lucide-react';
import type { MatterDeadline } from '../../lib/db';

interface DeadlineListProps {
  deadlines: MatterDeadline[];
  loading?: boolean;
  onDeadlineClick?: (deadline: MatterDeadline) => void;
  onMarkComplete?: (deadline: MatterDeadline) => void;
  showFilters?: boolean;
  groupByDate?: boolean;
}

type SortOption = 'date' | 'matter' | 'client' | 'priority';
type FilterOption = 'all' | 'overdue' | 'today' | 'thisWeek' | 'upcoming';

export function DeadlineList({
  deadlines,
  loading,
  onDeadlineClick,
  onMarkComplete,
  showFilters = true,
  groupByDate = true,
}: DeadlineListProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['overdue', 'today', 'tomorrow']));
  const [sortBy, setSortBy] = useState<SortOption>('date');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter deadlines
  const filteredDeadlines = useMemo(() => {
    let filtered = [...deadlines];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (d) =>
          d.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.matter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          d.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    const today = startOfDay(new Date());
    switch (filterBy) {
      case 'overdue':
        filtered = filtered.filter((d) => new Date(d.due_date) < today);
        break;
      case 'today':
        filtered = filtered.filter((d) => isToday(new Date(d.due_date)));
        break;
      case 'thisWeek':
        filtered = filtered.filter((d) => isThisWeek(new Date(d.due_date)));
        break;
      case 'upcoming':
        filtered = filtered.filter((d) => new Date(d.due_date) >= today);
        break;
    }

    return filtered;
  }, [deadlines, searchTerm, filterBy]);

  // Sort deadlines
  const sortedDeadlines = useMemo(() => {
    const sorted = [...filteredDeadlines];

    switch (sortBy) {
      case 'date':
        sorted.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
        break;
      case 'matter':
        sorted.sort((a, b) => (a.matter_name || '').localeCompare(b.matter_name || ''));
        break;
      case 'client':
        sorted.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
        break;
      case 'priority':
        // Assuming we might add priority field later
        sorted.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime());
        break;
    }

    return sorted;
  }, [filteredDeadlines, sortBy]);

  // Group deadlines by date
  const groupedDeadlines = useMemo(() => {
    if (!groupByDate) {
      return { 'All Deadlines': sortedDeadlines };
    }

    const groups: Record<string, MatterDeadline[]> = {};
    const today = startOfDay(new Date());

    sortedDeadlines.forEach((deadline) => {
      const dueDate = new Date(deadline.due_date);
      let groupKey: string;

      if (dueDate < today) {
        groupKey = 'overdue';
      } else if (isToday(dueDate)) {
        groupKey = 'today';
      } else if (isTomorrow(dueDate)) {
        groupKey = 'tomorrow';
      } else if (isThisWeek(dueDate)) {
        groupKey = 'thisWeek';
      } else {
        const monthYear = format(dueDate, 'MMMM yyyy');
        groupKey = monthYear;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(deadline);
    });

    return groups;
  }, [sortedDeadlines, groupByDate]);

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getGroupLabel = (groupKey: string) => {
    switch (groupKey) {
      case 'overdue':
        return 'Overdue';
      case 'today':
        return 'Today';
      case 'tomorrow':
        return 'Tomorrow';
      case 'thisWeek':
        return 'This Week';
      default:
        return groupKey;
    }
  };

  const getGroupColor = (groupKey: string) => {
    switch (groupKey) {
      case 'overdue':
        return 'text-red-600 bg-red-50';
      case 'today':
        return 'text-orange-600 bg-orange-50';
      case 'tomorrow':
        return 'text-blue-600 bg-blue-50';
      case 'thisWeek':
        return 'text-indigo-600 bg-indigo-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header with filters */}
      {showFilters && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search deadlines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>

            {/* Filter */}
            <div className="flex gap-2">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as FilterOption)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="all">All Deadlines</option>
                <option value="overdue">Overdue</option>
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="upcoming">Upcoming</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              >
                <option value="date">Sort by Date</option>
                <option value="matter">Sort by Matter</option>
                <option value="client">Sort by Client</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-2 text-sm text-gray-500">
            {filteredDeadlines.length} deadline{filteredDeadlines.length !== 1 ? 's' : ''} found
          </div>
        </div>
      )}

      {/* Deadline groups */}
      <div className="divide-y divide-gray-200">
        {Object.entries(groupedDeadlines).map(([groupKey, groupDeadlines]) => (
          <div key={groupKey}>
            {groupByDate && (
              <button
                onClick={() => toggleGroup(groupKey)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  {expandedGroups.has(groupKey) ? (
                    <ChevronDown className="w-4 h-4 mr-2 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mr-2 text-gray-400" />
                  )}
                  <span className={`px-2 py-1 rounded-md text-sm font-medium ${getGroupColor(groupKey)}`}>
                    {getGroupLabel(groupKey)}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">({groupDeadlines.length})</span>
                </div>
              </button>
            )}

            {(!groupByDate || expandedGroups.has(groupKey)) && (
              <div className="divide-y divide-gray-100">
                {groupDeadlines.map((deadline) => (
                  <div
                    key={deadline.id}
                    className="px-4 py-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <button
                        onClick={() => onDeadlineClick?.(deadline)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            {isPast(new Date(deadline.due_date)) && !deadline.is_completed ? (
                              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                            ) : deadline.is_completed ? (
                              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                            ) : (
                              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                            )}
                          </div>
                          <div className="ml-3 flex-1">
                            <p className={`text-sm font-medium ${deadline.is_completed ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                              {deadline.title}
                            </p>
                            <div className="mt-1 text-xs text-gray-500">
                              <span>{deadline.matter_name}</span>
                              {deadline.client_name && (
                                <>
                                  <span className="mx-1">•</span>
                                  <span>{deadline.client_name}</span>
                                </>
                              )}
                              {deadline.deadline_type && (
                                <>
                                  <span className="mx-1">•</span>
                                  <span className={`${
                                    deadline.deadline_type === 'task' ? 'text-indigo-600' :
                                    deadline.deadline_type === 'deliverable' ? 'text-purple-600' :
                                    deadline.deadline_type === 'project' ? 'text-green-600' :
                                    deadline.deadline_type === 'matter' ? 'text-blue-600' :
                                    'text-gray-600'
                                  }`}>
                                    {deadline.deadline_type === 'task' ? 'Task' :
                                     deadline.deadline_type === 'deliverable' ? 'Deliverable' :
                                     deadline.deadline_type === 'project' ? 'Project' :
                                     deadline.deadline_type === 'matter' ? 'Matter' :
                                     deadline.deadline_type === 'matter_deadline' ? 'Matter Deadline' :
                                     'Deadline'}
                                  </span>
                                </>
                              )}
                            </div>
                            {deadline.description && (
                              <p className="mt-1 text-xs text-gray-600 line-clamp-2">{deadline.description}</p>
                            )}
                          </div>
                        </div>
                      </button>

                      <div className="ml-4 flex items-center space-x-2">
                        <div className="text-right">
                          <p className="text-sm text-gray-900">
                            {format(new Date(deadline.due_date), 'MMM d, yyyy')}
                          </p>
                          <p className="text-xs text-gray-500">
                            {format(new Date(deadline.due_date), 'EEEE')}
                          </p>
                        </div>
                        {!deadline.is_completed && onMarkComplete && (
                          <button
                            onClick={() => onMarkComplete(deadline)}
                            className="p-1 rounded hover:bg-gray-200 transition-colors"
                            title="Mark as complete"
                          >
                            <CheckCircle className="w-4 h-4 text-gray-400 hover:text-green-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredDeadlines.length === 0 && (
          <div className="px-4 py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No deadlines found</p>
          </div>
        )}
      </div>
    </div>
  );
}